import axios from "axios";
import { useState, useEffect } from "react";
import {
  Container,
  Form,
  Button,
  Col,
  Row,
  Table,
  Spinner,
} from "react-bootstrap";
import { useSelector } from "react-redux";
import { saveAs } from "file-saver";
import { AnimatePresence, motion } from "framer-motion";
import "./Dashboard.scss";

const TestScenario = ({
  selectedTestCase,
  setSelectedTestCase,
  testScenarios,
  setStoreSingelRes,
  storeSingelRes,
  isRowSelected,
  handleCreateNewTestCase,
}) => {
  const [formData, setFormData] = useState({
    userStoryId: "",
    userStoryTitle: "",
    testScenario: "",
    method: "",
    file: null,
  });
  const [inputData, setInputData] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [testCaseGenerationRes, setTestCaseGenerationRes] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const userData = useSelector((state) => state.auth.userData);
  const IdToken = localStorage.getItem("msalIdToken");
  const Upload_API_URL = `${process.env.REACT_APP_API_URL}test_case_generation/upload_document`;
  const Create_TEST_SCENARIO = `${process.env.REACT_APP_API_URL}test_case_generation/generate_test_scenario`;
  const Generate_TEST_CASE = `${process.env.REACT_APP_API_URL}test_case_generation/generate_testcase_testscenario`;
  // Determine the data source for the table
  const dataToDisplay =
    selectedTestCase?.test_case?.length > 0
      ? selectedTestCase?.test_case
      : inputData?.generated_test_scenario;
  useEffect(() => {
    setCurrentPage(1);
    setSelectedRows([]);
  }, [dataToDisplay, selectedTestCase]);
  // Effect to update formData if a selectedTestCase is passed (for initial render)
  useEffect(() => {
    if (selectedTestCase) {
      setFormData({
        userStoryId: selectedTestCase.user_story_id || "",
        userStoryTitle: selectedTestCase.user_story_title || "",
        testScenario: selectedTestCase.user_story_description || "",
        method: selectedTestCase.method || "",
        file: null,
      });
      setInputData(null); // Clear inputData if we are loading from selectedTestCase
      setTestCaseGenerationRes(null); // Clear any previous test case generation results
    } else {
      // If no selectedTestCase, clear form data when going back to form
      setFormData({
        userStoryId: "",
        userStoryTitle: "",
        testScenario: "",
        method: "",
        file: null,
      });
    }
  }, [selectedTestCase]);
  const validateForm = () => {
    const newErrors = {};
    if (!formData.userStoryId.trim())
      newErrors.userStoryId = "User Story ID is required";
    if (!formData.userStoryTitle.trim())
      newErrors.userStoryTitle = "User Story Title is required";
    if (!formData.testScenario.trim())
      newErrors.testScenario = "Test Scenario is required";
    if (!formData.method) newErrors.method = "Method is required";
    if (formData.method === "Internal Documents" && !formData.file)
      newErrors.file = "File is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/csv",
    ];
    if (selectedFile && allowedTypes.includes(selectedFile.type)) {
      setFormData({ ...formData, file: selectedFile });
      setErrors((prev) => ({ ...prev, file: "" }));
    } else {
      setFormData({ ...formData, file: null });
      setErrors((prev) => ({ ...prev, file: "Invalid file type" }));
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsUploading(true);
    setProgress(0);
    setTestCaseGenerationRes(null);
    setSelectedRows([]);
    setStoreSingelRes(null);

    const payload = {
      user_id: userData?.mail,
      user_story_id: formData.userStoryId,
      user_story_title: formData.userStoryTitle,
      user_story_description: formData.testScenario,
      method: formData.method,
    };

    try {
      if (formData.method === "Internal Documents") {
        const fileData = new FormData();
        fileData.append("file", formData.file);
        await axios.post(
          `${Upload_API_URL}?user_id=${userData?.mail}`,
          fileData,
          {
            headers: {
              Authorization: `Bearer ${IdToken}`,
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (e) => {
              setProgress(Math.round((e.loaded / e.total) * 50));
            },
          }
        );
      }

      const response = await axios.post(Create_TEST_SCENARIO, payload, {
        headers: {
          Authorization: `Bearer ${IdToken}`,
          "Content-Type": "application/json",
        },
        onUploadProgress: (e) => {
          setProgress(Math.round((e.loaded / e.total) * 50 + 50));
        },
      });

      setInputData(response.data);
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setIsUploading(false);
      setProgress(100);
    }
  };
  const handleCreateNewTestScenario = () => {
    setFormData({
      userStoryId: "",
      userStoryTitle: "",
      testScenario: "",
      method: "",
      file: null,
    });
    setErrors({});
    setInputData(null);
    setSelectedRows([]);
    setProgress(0);
    setTestCaseGenerationRes(null);
    setSelectedTestCase(null);
    handleCreateNewTestCase();
    setStoreSingelRes(null);
  };
  const handleGenerateTestCase = async () => {
    const methodValue = storeSingelRes?.method || formData?.method;

    if (methodValue === "Internal Documents") {
      try {
        const fileData = new FormData();
        fileData.append("file", formData?.file || storeSingelRes?.file);
        await axios.post(
          `${Upload_API_URL}?user_id=${userData?.mail}`,
          fileData,
          {
            headers: {
              Authorization: `Bearer ${IdToken}`,
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (e) => {
              setProgress(Math.round((e.loaded / e.total) * 50));
            },
          }
        );
      } catch (err) {
        console.error("Error:", err);
      } finally {
        setIsUploading(false);
        setProgress(100);
      }
    }    
    const sourceData = selectedRows?.length > 0 ? selectedRows : dataToDisplay;

    if (!sourceData || sourceData?.length === 0) {
      console.warn("No test scenarios to generate test cases from.");
      return;
    }
    const userStoryId = storeSingelRes?.user_story_id || formData.userStoryId;
    const userStoryTitle =
      storeSingelRes?.user_story_title || formData.userStoryTitle;
    const userStoryDescription =
      storeSingelRes?.user_story_description || formData.testScenario;
    const method = storeSingelRes?.method || formData.method;

    const formattedTestCases = sourceData?.map((item) => ({
      "User Story ID": userStoryId,
      "User Story Title": userStoryTitle,
      "User Story Description": userStoryDescription,
      "Test Scenario ID": item["Test Scenario ID"],
      "Test Scenario Description": item["Test Scenario Description"] || "",
    }));

    const payload = {
      user_id: userData?.mail,
      user_story_id: userStoryId,
      user_story_title: userStoryTitle,
      user_story_description: userStoryDescription,
      test_cases: formattedTestCases,
      method: method,
    };

    try {
      setIsGenerating(true);
      const response = await axios.post(Generate_TEST_CASE, payload, {
        headers: {
          Authorization: `Bearer ${IdToken}`,
          "Content-Type": "application/json",
        },
      });
      setTestCaseGenerationRes(response.data);
    } catch (error) {
      console.error("Error generating test case:", error);
    } finally {
      setIsGenerating(false);
    }
  };
  const totalPages = Math.ceil((dataToDisplay?.length || 0) / rowsPerPage);
  const paginatedData = dataToDisplay?.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const tableHeaders = dataToDisplay?.length
    ? Object.keys(dataToDisplay[0])
    : [];
  const handleCopy = () => {
    const headers = tableHeaders;
    const rowsToCopy = selectedRows.length > 0 ? selectedRows : dataToDisplay;

    if (!rowsToCopy || rowsToCopy.length === 0) return;

    const content = [
      headers.join("\t"),
      ...rowsToCopy.map((row) => headers.map((h) => row[h]).join("\t")),
    ].join("\n");

    navigator.clipboard.writeText(content);
  };
  const handleExportCSV = () => {
    const headers = tableHeaders;
    const rowsToExport = selectedRows.length > 0 ? selectedRows : dataToDisplay;

    if (!rowsToExport || rowsToExport.length === 0) return;

    const csv = [
      headers.join(","),
      ...rowsToExport.map((row) =>
        headers
          .map((h) => `"${(row[h] ?? "").toString().replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(
      blob,
      `TestScenarios_${
        formData.userStoryId || selectedTestCase?.user_story_id || "generated"
      }.csv`
    );
  };
  const convertToCSV = (data) => {
    if (!data?.test_case_outputs || data.test_case_outputs.length === 0)
      return "";

    const rows = [];
    data.test_case_outputs.forEach((scenario) => {
      scenario.test_case.forEach((tc) => {
        rows.push({ "Test Scenario ID": scenario.test_scenario_id, ...tc });
      });
    });

    if (rows.length === 0) return "";

    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((row) =>
        headers
          .map((field) => `"${String(row[field] || "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");

    return csv;
  };
  const convertToText = (data) => {
    if (!data?.test_case_outputs || data.test_case_outputs.length === 0)
      return "";
    return data.test_case_outputs
      .map((scenario) => {
        return (
          `Scenario: ${scenario.test_scenario_id}\n` +
          scenario.test_case
            .map((tc) =>
              Object.entries(tc)
                .map(([k, v]) => `${k}: ${v}`)
                .join("\n")
            )
            .join("\n\n")
        );
      })
      .join("\n\n---\n\n");
  };
  const showTable = dataToDisplay && dataToDisplay?.length > 0;
  const displayUserStoryId =
    storeSingelRes?.user_story_id || formData.userStoryId;
  const displayUserStoryTitle =
    storeSingelRes?.user_story_title || formData.userStoryTitle;
  const displayMethod = storeSingelRes?.method || formData.method;
  const formatHeader = (key) => key.replace(/_/g, " ");
  const formatCellValue = (value) => {
    if (value === null || typeof value === "undefined") {
      return "";
    }
    return typeof value === "object" ? JSON.stringify(value) : value.toString();
  };
  // Ensure this logic is placed in your component before the return block
  const testCases = testCaseGenerationRes?.test_case_outputs || [];
  const groupedByScenario = testCases.reduce((acc, testCase) => {
    const scenarioId = testCase["Test Scenario ID"];
    if (!acc[scenarioId]) {
      acc[scenarioId] = [];
    }
    acc[scenarioId].push(testCase);
    return acc;
  }, {});
  return (
    <Container fluid className="p-4">
      <Row>
        {/* Main Content Column */}
        <Col md={testCaseGenerationRes ? 9 : 12}>
          {showTable ? (
            <>
              <div className="d-flex justify-content-between align-items-center rounded p-3 bg-white border mb-2">
                <div>
                  <h6 className="fw-bold">Generate Test Scenario</h6>
                  <p className="mb-0 text-muted">
                    <strong>User Story ID:</strong> {displayUserStoryId}
                  </p>

                  <div className="mb-1 d-flex flex-wrap align-items-start">
                    <strong className="me-2 text-muted">Title:</strong>
                    <div
                      style={{
                        maxWidth: "500px",
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: "normal",
                        color: "black",
                        flex: 1,
                      }}
                    >
                      {displayUserStoryTitle}
                    </div>
                  </div>

                  <p className="mb-0 text-muted">
                    <strong>Method:</strong> {displayMethod}
                  </p>
                </div>

                <div className="d-flex gap-2 align-items-center">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={handleCopy}
                  >
                    📋 Copy
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={handleExportCSV}
                  >
                    ⬇ Export - CSV
                  </Button>
                  <Button
                    size="sm"
                    // variant="primary"
                    className="proceed-btn"
                    onClick={handleGenerateTestCase}
                    disabled={
                      isGenerating ||
                      !formData?.method ||
                      !!storeSingelRes?.length ||
                      selectedRows?.length === 0
                    }
                  >
                    {isGenerating ? "Generating..." : "Generate Test Case"}
                  </Button>
                  <Button
                    size="sm"
                    variant="link"
                    className="text-decoration-none text-primary"
                    onClick={handleCreateNewTestScenario}
                  >
                    ← Back
                  </Button>
                </div>
              </div>

              <div
                className="rounded border bg-white p-3"
                style={{
                  maxHeight: "460px",
                  overflowY: "auto",
                  maxWidth: "70vw",
                }}
              >
                <Table striped bordered hover responsive>
                  <thead
                    style={{
                      position: "sticky",
                      top: 0,
                      background: "#f8f9fa",
                      zIndex: 2,
                    }}
                  >
                    <tr>
                      {/* Conditional checkbox column header */}
                      {formData.method === "" &&
                      typeof storeSingelRes === "object" &&
                      storeSingelRes !== null &&
                      Object.keys(storeSingelRes).length > 0 ? null : (
                        <th
                          style={{
                            whiteSpace: "nowrap",
                            borderBottom: "1px solid #dee2e6",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={
                              dataToDisplay?.length > 0 &&
                              selectedRows.length === dataToDisplay.length
                            }
                            onChange={(e) =>
                              setSelectedRows(
                                e.target.checked ? [...dataToDisplay] : []
                              )
                            }
                          />
                        </th>
                      )}

                      {/* Table headers */}
                      {tableHeaders?.map((header, index) => (
                        <th
                          key={index}
                          style={{
                            whiteSpace: "nowrap",
                            borderBottom: "1px solid #dee2e6",
                            fontWeight: "bold",
                            fontSize: "13px",
                          }}
                        >
                          {formatHeader(header)}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData?.map((row, idx) => {
                      const isChecked = selectedRows.includes(row);
                      return (
                        <tr key={idx}>
                          {/* Conditional checkbox column cell */}
                          {formData.method === "" &&
                          typeof storeSingelRes === "object" &&
                          storeSingelRes !== null &&
                          Object.keys(storeSingelRes).length > 0 ? null : (
                            <td>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() =>
                                  setSelectedRows(
                                    isChecked
                                      ? selectedRows.filter((r) => r !== row)
                                      : [...selectedRows, row]
                                  )
                                }
                              />
                            </td>
                          )}

                          {/* Row cells */}
                          {tableHeaders?.map((key, colIndex) => (
                            <td key={colIndex}>{formatCellValue(row[key])}</td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              <div className="d-flex justify-content-center align-items-center mt-3 gap-2">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                >
                  ← Prev
                </Button>
                {Array.from({ length: totalPages }, (_, index) => (
                  <Button
                    key={index}
                    size="sm"
                    variant={index + 1 === currentPage ? "primary" : "light"}
                    className="mx-1"
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </Button>
                ))}
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                >
                  Next →
                </Button>
              </div>
            </>
          ) : (
            <Form
              onSubmit={handleSubmit}
              className="p-3 bg-white rounded border"
            >
              <Form.Group className="mb-3">
                <Form.Label>User Story ID</Form.Label>
                <Form.Control
                  name="userStoryId"
                  value={formData?.userStoryId}
                  onChange={handleChange}
                  placeholder="Ex: US-123"
                  isInvalid={!!errors.userStoryId}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.userStoryId}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>User Story Title</Form.Label>
                <Form.Control
                  name="userStoryTitle"
                  value={formData?.userStoryTitle}
                  onChange={handleChange}
                  placeholder="Title of the user story"
                  isInvalid={!!errors.userStoryTitle}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.userStoryTitle}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Method</Form.Label>
                <Form.Select
                  name="method"
                  value={formData?.method}
                  onChange={handleChange}
                  isInvalid={!!errors.method}
                >
                  <option value="">Select Method</option>
                  <option value="AI Assistant">AI Assistant</option>
                  <option value="Internal Documents">Internal Documents</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.method}
                </Form.Control.Feedback>
              </Form.Group>

              {formData?.method === "Internal Documents" && (
                <Form.Group className="mb-3">
                  <Form.Label>Upload File</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={handleFileChange}
                    accept=".doc,.docx,.pdf,.csv,.xlsx"
                    isInvalid={!!errors.file}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.file}
                  </Form.Control.Feedback>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Test Scenario Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="testScenario"
                  rows={4}
                  value={formData?.testScenario}
                  onChange={handleChange}
                  placeholder="Enter test scenario..."
                  isInvalid={!!errors.testScenario}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.testScenario}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="d-flex align-items-center gap-2 mt-2">
                <Button
                  type="submit"
                  className="proceed-btn"
                  disabled={isUploading}
                >
                  {isUploading ? "Processing..." : "Proceed"}
                </Button>

                <Button
                  className="create-new-btn px-3 ms-2"
                  variant="outline-secondary"
                  onClick={handleCreateNewTestScenario}
                  disabled={isUploading}
                >
                  Reset & Create New
                </Button>

                {isUploading && (
                  <div className="d-flex align-items-center ms-2">
                    <progress
                      value={progress}
                      max="100"
                      style={{
                        height: "6px",
                        width: "100px",
                        marginLeft: "10px",
                        verticalAlign: "middle",
                      }}
                    />
                    <span className="ms-2 text-muted small">{progress}%</span>
                  </div>
                )}
              </Form.Group>
            </Form>
          )}
        </Col>

        {/* Right Side Response Panel - Conditionally Rendered */}
        {testCaseGenerationRes && (
          <Col md={3}>
            <AnimatePresence>
              {(isGenerating || testCaseGenerationRes) && (
                <motion.div
                  key="response-panel"
                  className="position-fixed top-0 end-0 shadow-lg bg-white border rounded"
                  initial={{ opacity: 0, x: 300 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 300 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    width: "400px",
                    height: "100vh",
                    zIndex: 1050,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Fixed Header */}
                  <div
                    className="d-flex justify-content-between align-items-center p-3 border-bottom bg-white"
                    style={{ position: "sticky", top: 0, zIndex: 10 }}
                  >
                    <h6 className="fw-bold mb-0">Test Cases</h6>
                    <div className="d-flex gap-2 align-items-center">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => {
                          const text = testCases
                            .map((tc) =>
                              Object.entries(tc)
                                .map(([key, val]) => `${key}: ${val}`)
                                .join("\n")
                            )
                            .join("\n\n--------------------------\n\n");
                          navigator.clipboard.writeText(text);
                        }}
                        disabled={isGenerating || !testCaseGenerationRes}
                      >
                        📋
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => {
                          const headers = Object.keys(testCases[0]);
                          const csvRows = [
                            headers.join(","),
                            ...testCases.map((row) =>
                              headers
                                .map(
                                  (h) =>
                                    `"${(row[h] || "")
                                      .toString()
                                      .replace(/"/g, '""')}"`
                                )
                                .join(",")
                            ),
                          ];
                          const blob = new Blob([csvRows.join("\n")], {
                            type: "text/csv",
                          });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = "test_cases.csv";
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                        disabled={isGenerating || !testCaseGenerationRes}
                      >
                        ⬇
                      </Button>
                      <Button
                        size="sm"
                        variant="light"
                        onClick={() => setTestCaseGenerationRes(null)}
                        style={{
                          fontWeight: "bold",
                          fontSize: "16px",
                          color: "black",
                          lineHeight: "1",
                          padding: "0 6px",
                        }}
                      >
                        ✕
                      </Button>
                    </div>
                  </div>

                  {/* Scrollable Content */}
                  <div
                    className="p-3 overflow-auto"
                    style={{ flex: 1, fontSize: "13px" }}
                  >
                    {isGenerating ? (
                      <div
                        className="d-flex justify-content-center align-items-center"
                        style={{ height: "100%" }}
                      >
                        <Spinner animation="border" variant="primary" />
                      </div>
                    ) : (
                      <>
                        {testCases.length > 0 ? (
                          <>
                            {/* Copy and Export Buttons */}
                            {/* <div className="d-flex justify-content-end gap-2 mb-3">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                const text = testCases
                  .map(tc =>
                    Object.entries(tc)
                      .map(([key, val]) => `${key}: ${val}`)
                      .join("\n")
                  )
                  .join("\n\n--------------------------\n\n");
                navigator.clipboard.writeText(text);
              }}
            >
              Copy
            </Button>

            <Button
              variant="outline-success"
              size="sm"
              onClick={() => {
                const headers = Object.keys(testCases[0]);
                const csvRows = [
                  headers.join(","),
                  ...testCases.map(row =>
                    headers.map(h => `"${(row[h] || "").toString().replace(/"/g, '""')}"`).join(",")
                  ),
                ];
                const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "test_cases.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export CSV
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setTestCaseGenerationRes(null)}
            >
              X
            </Button>
          </div> */}

                            {/* Grouped Test Case Display */}
                            {Object.entries(groupedByScenario).map(
                              ([scenarioId, testCases], i) => (
                                <div key={i} className="mb-4">
                                  <div className="fw-bold mb-2">
                                    Scenario: {scenarioId}
                                  </div>
                                  {testCases.map((testCase, j) => (
                                    <div
                                      key={j}
                                      className="border rounded p-2 mb-3 bg-light"
                                      style={{ whiteSpace: "pre-wrap" }}
                                    >
                                      {Object.entries(testCase).map(
                                        ([key, value], k) => (
                                          <div key={k} className="mb-2">
                                            <strong>{key}:</strong>{" "}
                                            <span>{value}</span>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )
                            )}
                          </>
                        ) : (
                          <div className="text-center text-muted">
                            No test cases generated yet.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Col>
        )}
      </Row>
    </Container>
  );
};
export default TestScenario;
